import config from "../seo-config.json";
import {
  localized,
  projects,
  publications,
  publishedArtworks,
} from "./catalog";
import { type Locale, messages } from "./i18n";

// Only a reviewed custom origin may become the canonical host. A missing
// origin means a preview build: no canonical/og:url and no indexing.
export function canonicalOrigin(value: unknown): string | null {
  if (value === null) return null;
  if (typeof value !== "string")
    throw new Error("SEO siteUrl must be null or an HTTPS custom origin");
  const url = new URL(value);
  if (
    url.protocol !== "https:" ||
    url.username ||
    url.password ||
    url.port ||
    url.pathname !== "/" ||
    url.search ||
    url.hash ||
    !url.hostname.includes(".") ||
    url.hostname.endsWith(".pages.dev") ||
    url.hostname === "pages.dev" ||
    url.hostname.endsWith(".localhost") ||
    url.hostname.endsWith(".invalid") ||
    url.hostname.endsWith(".test") ||
    url.hostname.endsWith(".example") ||
    /^\d+\.\d+\.\d+\.\d+$/.test(url.hostname)
  )
    throw new Error(
      "SEO siteUrl requires a reviewed HTTPS custom origin, not a preview host or path",
    );
  return url.origin;
}
export const siteOrigin = canonicalOrigin(config.siteUrl);
// Social images need a public absolute URL even while pages remain noindex.
// This delivery host is not a canonical/indexing decision.
const imageHost = new URL(config.imageOrigin);
if (
  imageHost.protocol !== "https:" ||
  imageHost.username ||
  imageHost.password ||
  imageHost.port ||
  imageHost.pathname !== "/" ||
  imageHost.search ||
  imageHost.hash
)
  throw new Error("SEO imageOrigin must be a public HTTPS origin");
export const imageOrigin = imageHost.origin;
const socialImagePath = "/assets/ausra-maciulyte-og.jpg";
export const seoPaths = [
  "/",
  "/works/",
  "/projects/",
  "/articles/",
  "/about/",
  "/contact/",
  ...publishedArtworks.map((item) => `/works/${item.slug}/`),
  ...projects.map((item) => `/projects/${item.slug}/`),
  ...publications.map((item) => `/articles/${item.slug}/`),
];
// Keep search/document titles intact; social cards use a shorter home label.
const homeOgTitles = {
  lt: "Aušra Mačiulytė · Tapyba, keramika, piešiniai, projektai",
  en: "Aušra Mačiulytė · Painting, ceramics, drawings & projects",
};
const sectionDescriptions = {
  lt: {
    home: "Aušros Mačiulytės kūryba: tapyba, keramika, piešiniai, Fluxus knygelės, kūrybiniai projektai ir publikacijos.",
    works:
      "Aušros Mačiulytės darbai: tapyba, keramika, piešiniai, Fluxus knygelės ir grafika. Kūriniai, jų nuotraukos ir autorės pateikti duomenys.",
    projects:
      "Aušros Mačiulytės kūrybiniai projektai: Burtų miškas, A.M. ūkis, baltiškas masažas ir lietuviškos runos.",
    articles:
      "Aušros Mačiulytės pateiktas publikacijų archyvas: santraukos, originalūs žurnalų ir laikraščio „Draugas“ puslapiai bei PDF dokumentai.",
    about:
      "Apie Aušrą Mačiulytę: tapybos, keramikos ir piešinių kūryba, studijos, parodos, kultūriniai renginiai ir meno terapijos veikla.",
    contact:
      "Susisiekite su Aušra Mačiulyte dėl kūrybos ir projektų. Kontaktų forma užklausoms ir žinutėms.",
  },
  en: {
    home: "Art by Aušra Mačiulytė: painting, ceramics, drawings, Fluxus booklets, creative projects and publications.",
    works:
      "Works by Aušra Mačiulytė: painting, ceramics, drawings, Fluxus booklets and graphic works, with photographs and artist-supplied details.",
    projects:
      "Creative projects by Aušra Mačiulytė: Forest of Spells, A.M. ūkis, Baltic massage and Lithuanian runes.",
    articles:
      "Publication archive supplied by Aušra Mačiulytė: summaries, original magazine and Draugas newspaper pages, and PDF documents.",
    about:
      "About Aušra Mačiulytė: painting, ceramics and drawings, studies, exhibitions, cultural events and art-therapy practice.",
    contact:
      "Contact Aušra Mačiulytė about her art and creative projects. An enquiry form for questions and messages.",
  },
};
function concise(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length <= 175
    ? clean
    : `${clean.slice(0, 172).replace(/\s+\S*$/, "")}…`;
}
export function pageSeo(path: string, locale: Locale, origin = siteOrigin) {
  const cleanPath =
    path.replace(/\/index\.html$/, "/").replace(/\/+$/, "") || "/";
  const normalized = cleanPath === "/" ? "/" : `${cleanPath}/`;
  const [, section = "home", slug] = normalized.split("/");
  const artwork =
    section === "works"
      ? publishedArtworks.find((item) => item.slug === slug)
      : undefined;
  const project =
    section === "projects"
      ? projects.find((item) => item.slug === slug)
      : undefined;
  const publication =
    section === "articles"
      ? publications.find((item) => item.slug === slug)
      : undefined;
  const entry = artwork || project || publication;
  const key =
    normalized === "/"
      ? "home"
      : (section as keyof typeof sectionDescriptions.lt);
  const known = seoPaths.includes(normalized);
  const name = entry
    ? localized(entry.title, locale) || messages[locale].title
    : known
      ? messages[locale][key]
      : messages[locale].notFound;
  const title =
    key === "home" && known
      ? `${messages[locale].title} · ${messages[locale].intro.replace(/\.$/, "")}`
      : `${name} · ${messages[locale].title}`;
  const description = concise(
    artwork
      ? `${name} — ${messages[locale].title}. ${[artwork.year, localized(artwork.technique, locale), localized(artwork.description, locale)].filter(Boolean).join(" · ") || messages[locale][artwork.category]}`
      : project
        ? `${name} — ${messages[locale].title}. ${localized(project.intro, locale)}`
        : publication
          ? `${name}. ${localized(publication.summary, locale)}`
          : known
            ? sectionDescriptions[locale][key]
            : messages[locale].notFound,
  );
  const canonical = known && origin ? `${origin}${normalized}` : null;
  return {
    title,
    ogTitle: key === "home" && known ? homeOgTitles[locale] : title,
    image: {
      // The supplied emblem's full frame is fitted, never cropped, into a
      // JPEG social card emitted independently of the gallery/publicDir mode.
      url: new URL(socialImagePath, origin || imageOrigin).href,
      width: 1200,
      height: 630,
      type: "image/jpeg",
      alt: messages[locale].logoAlt,
    },
    description,
    canonical,
    robots: known && origin ? "index, follow" : "noindex, follow",
    structured: {
      "@context": "https://schema.org",
      "@type": key === "about" ? "AboutPage" : "WebPage",
      name: title,
      description,
      inLanguage: locale,
      ...(canonical ? { url: canonical } : {}),
      ...(key === "home" || key === "about"
        ? { about: { "@type": "Person", name: messages[locale].title } }
        : {}),
    },
  };
}
export function updatePageSeo(path: string, locale: Locale) {
  const seo = pageSeo(path, locale);
  document.title = seo.title;
  document.documentElement.lang = locale;
  function meta(attribute: "name" | "property", key: string, content: string) {
    let node = document.head.querySelector<HTMLMetaElement>(
      `meta[${attribute}="${key}"]`,
    );
    if (!node) {
      node = document.createElement("meta");
      node.setAttribute(attribute, key);
      document.head.append(node);
    }
    node.content = content;
  }
  meta("name", "description", seo.description);
  meta("name", "robots", seo.robots);
  meta("property", "og:title", seo.ogTitle);
  meta("property", "og:description", seo.description);
  meta("property", "og:type", "website");
  meta("property", "og:image", seo.image.url);
  meta("property", "og:image:width", String(seo.image.width));
  meta("property", "og:image:height", String(seo.image.height));
  meta("property", "og:image:type", seo.image.type);
  meta("property", "og:image:alt", seo.image.alt);
  meta("property", "og:site_name", messages[locale].title);
  meta("name", "twitter:card", "summary");
  meta("name", "twitter:title", seo.title);
  meta("name", "twitter:description", seo.description);
  let canonical = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (seo.canonical) {
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.rel = "canonical";
      document.head.append(canonical);
    }
    canonical.href = seo.canonical;
    meta("property", "og:url", seo.canonical);
  } else {
    canonical?.remove();
    document.head.querySelector('meta[property="og:url"]')?.remove();
  }
  let schema = document.head.querySelector<HTMLScriptElement>("#page-schema");
  if (!schema) {
    schema = document.createElement("script");
    schema.id = "page-schema";
    schema.type = "application/ld+json";
    document.head.append(schema);
  }
  schema.textContent = JSON.stringify(seo.structured);
}
